// Dock wrap-up sheet (DESIGN.md "Session · Wrap-up", compact form): shown by
// the call dock after a call ends outside a session, where the live bar was.
// Outcome keys 1–6, notes, a next step, Save. It reuses the disposition
// contract the Dialer page uses: one atomic save of outcome + notes +
// variables.workflow, and the sheet only closes after the save succeeds. A
// failed save keeps every entry. Closing with entries asks first.

import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import callApi from "../../services/callApi";
import {
  EMPTY_DISPOSITION,
  FOLLOW_UP_ACTIONS,
  WRAP_OUTCOMES,
  validateDisposition,
} from "../ui/CallDispositionForm";
import { XIcon } from "./icons";

/**
 * The workflow payload the disposition endpoint reads, shared with Dialer.
 * The follow-up goes only when one is set: the backend treats an explicit
 * null followUpAt as "clear the contact's next action date", so sending it
 * for every save would wipe a date set elsewhere.
 */
export const buildWrapPayload = (data, call = {}) => {
  const workflow = {
    ...(call.contactId ? { contactId: call.contactId } : {}),
    ...(call.campaignId ? { campaignId: call.campaignId } : {}),
    ...(data.followUpDate
      ? { followUpAction: data.followUpAction || null, followUpAt: data.followUpDate }
      : {}),
    ...(data.statusChange ? { contactStatus: data.statusChange } : {}),
  };
  return {
    outcome: data.outcome,
    notes: data.notes,
    ...(Object.keys(workflow).length > 0 ? { variables: { workflow } } : {}),
  };
};

/** Tomorrow as YYYY-MM-DD in local time, the default for a callback. */
export const tomorrowISODate = (now = new Date()) => {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
};

const FAILED_SAVE = "Wrap-up was not saved. Your entries are kept; retry when ready.";

/**
 * A failed save in words a rep can act on: the server's own message when it
 * sent one, otherwise a plain sentence. Never the transport's status text.
 */
export const describeSaveError = (error) => {
  const body = error?.response?.data;
  const candidates = [body?.error, body?.message, body?.error?.message];
  const message = candidates.find((value) => typeof value === "string" && value.trim());
  if (!message) return FAILED_SAVE;
  return `Wrap-up was not saved: ${message.trim().replace(/\.$/, "")}. Your entries are kept.`;
};

const EDITABLE = "input, textarea, select, [contenteditable=''], [contenteditable='true']";
const otherDialogOpen = () => Boolean(document.querySelector("[role='dialog'][aria-modal='true']"));

const DockWrapSheet = ({ call = {}, durationLabel = "", onDone, onDismiss }) => {
  const [data, setData] = useState(EMPTY_DISPOSITION);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const sheetRef = useRef(null);
  const keepEditingRef = useRef(null);
  const canSave = Boolean(call.id);
  const party = call.name || call.number || "Last call";
  const dirty = Boolean(data.outcome || data.notes.trim() || data.followUpAction || data.followUpDate);

  const set = (name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  // A callback needs a when: picking it proposes "Call · tomorrow", which the
  // rep can change but not leave empty (validateDisposition).
  const pickOutcome = (value) => {
    setData((prev) => {
      const next = { ...prev, outcome: value };
      if (value === "callback") {
        if (!next.followUpAction) next.followUpAction = "call";
        if (!next.followUpDate) next.followUpDate = tomorrowISODate();
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, outcome: null, followUpAction: null, followUpDate: null }));
  };

  const save = async () => {
    if (saving) return;
    const nextErrors = validateDisposition(data);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!canSave) {
      setSaveError("This call has no tracked record, so there is nothing to save it against.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await callApi.saveDisposition(call.id, buildWrapPayload(data, call));
      toast.success("Wrap-up saved.");
      onDone?.();
    } catch (error) {
      setSaveError(describeSaveError(error));
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onDismiss?.();
  };

  useEffect(() => {
    if (confirmDiscard) keepEditingRef.current?.focus();
  }, [confirmDiscard]);

  // Keys 1–6 pick an outcome while focus is not in a field; Enter saves when
  // focus is inside the sheet; Escape closes (asking first when there are
  // entries). All of it stands down while another dialog is open.
  const keyRef = useRef(null);
  useEffect(() => {
    keyRef.current = { save, pickOutcome, requestClose, confirmDiscard };
  });
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (otherDialogOpen()) return;
      const target = event.target;
      const typing = target instanceof Element && target.closest(EDITABLE);
      const inSheet = target instanceof Node && sheetRef.current?.contains(target);
      const keys = keyRef.current;

      if (event.key === "Escape") {
        event.preventDefault();
        if (keys.confirmDiscard) setConfirmDiscard(false);
        else keys.requestClose();
        return;
      }
      if (keys.confirmDiscard) return;
      if (!typing) {
        const match = WRAP_OUTCOMES.find((option) => option.key === event.key);
        if (match) {
          event.preventDefault();
          keys.pickOutcome(match.value);
          return;
        }
      }
      // Inside a field the form submits itself; on other parts of the sheet
      // Enter saves. Buttons and links keep their own Enter.
      if (event.key === "Enter" && inSheet && !typing) {
        if (target instanceof Element && target.closest("button, a")) return;
        event.preventDefault();
        keys.save();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <section
      ref={sheetRef}
      className="sg-dock sg-dock-sheet sg-pop"
      data-state="wrap"
      aria-label="Wrap up last call"
    >
      <header className="sg-dock-sheet-head">
        <div className="sg-dock-party">
          <span className="sg-dock-title">Wrap up · {party}</span>
          <span className="sg-dock-meta">
            {call.number && call.number !== party && <span className="sg-dock-number">{call.number}</span>}
            <span>{durationLabel ? `Ended · ${durationLabel}` : "Ended"}</span>
          </span>
        </div>
        <button
          type="button"
          className="sg-dock-btn sg-dock-close"
          aria-label="Close wrap-up"
          data-hint="Close wrap-up"
          data-hint-keys="Esc"
          aria-keyshortcuts="Escape"
          onClick={requestClose}
          disabled={saving}
        >
          <XIcon size={14} />
        </button>
      </header>

      <form
        className="sg-dock-sheet-body"
        aria-busy={saving}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <fieldset disabled={saving || confirmDiscard} className="min-w-0">
          <legend className="sg-micro mb-2">Outcome</legend>
          <div className="sg-wrap-outcomes" role="radiogroup" aria-label="Outcome">
            {WRAP_OUTCOMES.map((option) => {
              const checked = data.outcome === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  aria-keyshortcuts={option.key}
                  className="sg-wrap-outcome"
                  data-tone={option.tone}
                  onClick={() => pickOutcome(option.value)}
                >
                  <kbd className="sg-dock-key" aria-hidden="true">
                    {option.key}
                  </kbd>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          {errors.outcome && (
            <p className="sg-dock-error" role="alert">
              Pick an outcome (keys 1–6).
            </p>
          )}

          <label className="sg-field-label mt-3" htmlFor="dock-wrap-notes">
            Notes
          </label>
          <textarea
            id="dock-wrap-notes"
            className="sg-field"
            rows={3}
            value={data.notes}
            placeholder="What was said, what was agreed"
            onChange={(event) => set("notes", event.target.value)}
          />

          <div className="sg-wrap-next">
            <div>
              <label className="sg-field-label" htmlFor="dock-wrap-action">
                Next step
              </label>
              <select
                id="dock-wrap-action"
                className="sg-field"
                value={data.followUpAction}
                aria-invalid={Boolean(errors.followUpAction) || undefined}
                onChange={(event) => set("followUpAction", event.target.value)}
              >
                {FOLLOW_UP_ACTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {/* The contact keeps a date, not a time, so the field asks for a date. */}
              <label className="sg-field-label" htmlFor="dock-wrap-date">
                Follow-up date
              </label>
              <input
                id="dock-wrap-date"
                className="sg-field"
                type="date"
                value={data.followUpDate}
                aria-invalid={Boolean(errors.followUpDate) || undefined}
                onChange={(event) => set("followUpDate", event.target.value)}
              />
            </div>
          </div>
          {(errors.followUpAction || errors.followUpDate) && (
            <p className="sg-dock-error" role="alert">
              {errors.followUpAction || errors.followUpDate}
            </p>
          )}

          {!canSave && (
            <p className="sg-dock-note">
              No tracked record for this call. Outcomes can only be saved against a tracked call.
            </p>
          )}
          {saveError && (
            <div role="alert" className="sg-alert mt-3" data-tone="danger">
              {saveError}
            </div>
          )}

          {!confirmDiscard && (
            <div className="sg-dock-sheet-foot">
              <button type="button" className="sg-btn sg-btn-ghost sg-btn-sm" onClick={requestClose}>
                Close
              </button>
              <button type="submit" className="sg-btn sg-btn-primary sg-btn-sm" disabled={!canSave}>
                {saving ? "Saving…" : "Save"}
                {!saving && <kbd className="sg-kbd">Enter</kbd>}
              </button>
            </div>
          )}
        </fieldset>

        {confirmDiscard && (
          <div className="sg-dock-confirm" role="alertdialog" aria-label="Discard this wrap-up?">
            <p>Discard this wrap-up? The outcome and notes you entered will be lost.</p>
            <div className="sg-dock-sheet-foot">
              <button
                ref={keepEditingRef}
                type="button"
                className="sg-btn sg-btn-secondary sg-btn-sm"
                onClick={() => setConfirmDiscard(false)}
              >
                Keep editing
              </button>
              <button type="button" className="sg-btn sg-btn-danger sg-btn-sm" onClick={() => onDismiss?.()}>
                Discard
              </button>
            </div>
          </div>
        )}
      </form>
    </section>
  );
};

export default DockWrapSheet;
